import { z } from 'zod';
import { StylePresetSchema } from './styles.js';

/**
 * What the client sends to /amplify.
 *
 * The sketch is the user's raw input — typically a sentence fragment. We give
 * Claude minimal hints: the style preset (drives the per-style guide injection)
 * and an optional `aspectRatio` for downstream image generation.
 */
export const AmplifyRequestSchema = z.object({
  sketch: z.string().min(1).max(600),
  stylePreset: StylePresetSchema,
  aspectRatio: z
    .enum(['1:1', '3:2', '2:3', '16:9', '9:16'])
    .default('1:1'),
  /**
   * True when the generate request will pass a reference image (the user's
   * NFT). When true, the amplifier writes a CONCISE EDIT INSTRUCTION rather
   * than a flowery scene description — the reference image is the immutable
   * source of truth for the subject; the prompt only describes the scene.
   */
  hasReference: z.boolean().default(false),
  /**
   * The reference image URL (multimodal amplifier input). When supplied,
   * Claude SEES the actual character and can enumerate visible traits by
   * name ("pink bandana with left-side tassel, pixel sunglasses, pearled
   * vest with gold trim") instead of writing generic preservation language.
   * Has no effect when hasReference is false.
   */
  referenceImageUrl: z.string().url().nullable().default(null),
});
export type AmplifyRequest = z.infer<typeof AmplifyRequestSchema>;

/**
 * What Claude returns. The amplifier produces a canonical English-language
 * prompt and per-model adaptations (different models prefer different prompt
 * shapes — FLUX likes natural prose, GPT-Image likes structured tags, Grok
 * tolerates either).
 */
export const AmplifyResponseSchema = z.object({
  amplified: z.string().min(1),
  negative: z.string(),
  perModel: z.object({
    flux: z.string().min(1),
    gptImage: z.string().min(1),
    grok: z.string().min(1),
  }),
  /** Filled when Claude refuses the brief; the rest of the response is empty. */
  refused: z
    .object({
      reason: z.enum(['ip', 'nsfw', 'celebrity', 'harassment', 'unsafe', 'unclear']),
      message: z.string(),
    })
    .nullable(),
  /** Echo of the inputs so cache keys remain self-describing. */
  cacheKey: z.string(),
});
export type AmplifyResponse = z.infer<typeof AmplifyResponseSchema>;
